'use client';

import { useRouter as useNextRouter, useParams, useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import React, { startTransition, useEffect, useState } from 'react';

import { HrefOrHrefWithParams } from 'next-intl/dist/types/navigation/shared/utils';

import { WEBAPP_URL } from '@/constants/http-constants';
import { Locale } from '@/constants/locale-constants';
import { ESSENTIAL_COOKIES_PROPERTY_NAME } from '@cineamo/ui/src/constants/cookie-constants';

import { getExternalLinkTarget } from '@/helpers/external-link-helper';
import { getLanguageCodeFromLocale, getLanguageNameFromLocale, getLocaleFromLanguage } from '@/helpers/locale-helper';
import { getFormattedPhoneAndURI } from '@/helpers/phone-number-helper';
import { addUtmParams } from '@/helpers/utm-helper';
import { cn } from '@cineamo/ui/src/helpers/cn';
import { cookieStorage } from '@cineamo/ui/src/helpers/cookie-helper';

import BasicDropdown, { BasicDropdownOption } from '@cineamo/ui/src/dropdown/BasicDropdown';
import IconAppLogo from '@cineamo/ui/src/icons/ic-appLogo';
import { IconAutoMode24 } from '@cineamo/ui/src/icons/ic-autoMode';
import { IconBell24 } from '@cineamo/ui/src/icons/ic-bell.tsx';
import { IconDarkMode24 } from '@cineamo/ui/src/icons/ic-darkMode';
import { IconFacebook24 } from '@cineamo/ui/src/icons/ic-facebook';
import { IconGlobe16 } from '@cineamo/ui/src/icons/ic-globe';
import { IconInstagramNoBox24 } from '@cineamo/ui/src/icons/ic-instagram';
import { IconLightMode24 } from '@cineamo/ui/src/icons/ic-lightMode';
import { IconLinkedin24 } from '@cineamo/ui/src/icons/ic-linkedin';
import { IconMail24 } from '@cineamo/ui/src/icons/ic-mail';
import { IconPhone24 } from '@cineamo/ui/src/icons/ic-phone';
import { IconTiktok24 } from '@cineamo/ui/src/icons/ic-tiktok';
import { IconWhatsApp30 } from '@cineamo/ui/src/icons/ic-whatsApp';
import { IconX24 } from '@cineamo/ui/src/icons/ic-x';
import { CookiePreferencesModal } from '@cineamo/ui/src/modals/CookiePreferencesModal';
import CookiesModal from '@cineamo/ui/src/modals/CookiesModal';

import CenterLayout from '@cineamo/composables/src/CenterLayout';
import MobileOrDesktopRenderer from '@cineamo/composables/src/MobileOrDesktopRenderer';

import { locales } from '@/i18n/i18n.config';
import { Link, usePathname, useRouter } from '@/i18n/routing';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { useNewsletterModal } from '@/providers/NewsletterModalProvider';
import {
    COLOR_THEME_PREFERENCES,
    ColorThemePreference,
    hasDarkStyle,
    setColorThemePreferenceCookie
} from '@/utils/color-theme';

export default function Footer() {
    const t = useTranslations();
    const domainConfiguration = useDomainConfiguration();
    const currentLocale = useLocale();
    const router = useRouter();
    const currentPath = usePathname();
    const params = useParams();
    const searchParams = useSearchParams();
    const queryParams: { [key: string]: string } = {};
    searchParams.forEach((value, key) => {
        queryParams[key] = value;
    });

    const cinema = domainConfiguration.cinema;

    const websiteConfiguration = cinema?._embedded?.cinemaConfiguration?.websiteConfiguration;
    const cinemaHasCustomImprint = (websiteConfiguration?.imprints?.length ?? 0) > 0 || !!websiteConfiguration?.imprint;
    const cinemaHasTermsAndConditions =
        !!cinema?._embedded?.cinemaConfiguration?.websiteConfiguration?.termsAndConditionsUrl;
    const withdrawalUrl = websiteConfiguration?.withdrawalUrl;

    // Crawlable link back to the city/chain hub. The header dropdown carries the same URL, but only
    // inside a popover panel that is not server-rendered. Linked exactly as configured: many of these
    // URLs are not Cineamo jump pages (hotels, chain sites) and have no `/<locale>` route, and a
    // protocol-less value would become a broken relative link, so only absolute http(s) URLs qualify.
    const partnerLinksDropdownConfiguration = domainConfiguration.websiteConfiguration.partnerLinksDropdown;
    const hubUrl = partnerLinksDropdownConfiguration?.isVisible
        ? partnerLinksDropdownConfiguration.cinemaChainUrl
        : undefined;
    const hubHref = hubUrl && /^https?:\/\//i.test(hubUrl) && URL.canParse(hubUrl) ? hubUrl : undefined;
    const hubLabel =
        partnerLinksDropdownConfiguration?.cinemaChainLabel ??
        (cinema?.city ? t('footer.all-cinemas-in-city', { city: cinema.city }) : t('footer.all-cinemas'));

    const [cookiePreferencesModalIsOpen, setCookiePreferencesModalIsOpen] = useState(false);
    const [cookiesModalIsOpen, setCookiesModalIsOpen] = useState(false);
    const [cookiePreferencesOpenedFromCookiesModal, setCookiePreferencesOpenedFromCookiesModal] = useState(false);

    // Automatically show the cookie banner on the first visit, i.e. as long as no consent decision has been saved.
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (!cookieStorage.getItem(ESSENTIAL_COOKIES_PROPERTY_NAME)) {
                setCookiesModalIsOpen(true);
            }
        }, 1500);
        return () => clearTimeout(timeout);
    }, []);

    // A cinema website has no `/data-protection` route of its own, so the consent modals link to the
    // web app's document instead of the shared components' in-app default.
    function dataProtectionUrl(utmContent: string) {
        return addUtmParams(`${WEBAPP_URL}/data-protection?locale=${currentLocale}`, {
            utmSource: 'cinema_website',
            utmContent: utmContent,
            utmCampaign: `cinema_${cinema.id}-${cinema.name}`,
            utmMedium: 'website'
        });
    }

    function onShowCookiePreferencesFromCookiesModal() {
        setCookiesModalIsOpen(false);
        setCookiePreferencesOpenedFromCookiesModal(true);
        setCookiePreferencesModalIsOpen(true);
    }

    const languageDropdownOptions: BasicDropdownOption<Locale>[] = locales.map((locale) => {
        const loc = getLocaleFromLanguage(locale);
        return {
            value: loc,
            label: `${getLanguageCodeFromLocale(loc).toUpperCase()} - ${getLanguageNameFromLocale(loc)}`
        };
    });

    function onLanguageChange(selectedLocale: Locale) {
        const newLocale = getLanguageCodeFromLocale(selectedLocale);
        startTransition(() => {
            router.replace(
                {
                    pathname: currentPath,
                    query: {
                        ...queryParams
                    },
                    params: {
                        ...params
                    }
                } as HrefOrHrefWithParams<string>,
                {
                    locale: newLocale,
                    scroll: false
                }
            );
        });
    }

    const nextRouter = useNextRouter();

    // Optimistic — the server-resolved preference only arrives with the refresh.
    const [selectedColorThemePreference, setSelectedColorThemePreference] = useState<ColorThemePreference>(
        domainConfiguration.colorThemePreference ?? 'auto'
    );

    function onColorThemeChange(preference: ColorThemePreference) {
        setSelectedColorThemePreference(preference);
        setColorThemePreferenceCookie(preference);
        // The theme is resolved on the server (the palette is server-rendered),
        // so re-render the current route with the new cookie.
        startTransition(() => {
            nextRouter.refresh();
        });
    }

    function onDataProtectionSettingsClick() {
        setCookiePreferencesOpenedFromCookiesModal(false);
        setCookiePreferencesModalIsOpen(true);
    }

    const externalLinkTarget = getExternalLinkTarget(websiteConfiguration);

    const footerItemCineamo = (
        <div className="flex flex-row space-x-8 items-center">
            <a
                aria-label="Cineamo"
                href={addUtmParams(WEBAPP_URL ?? '', {
                    utmSource: 'cinema_website',
                    utmContent: 'footer-cineamo_logo',
                    utmCampaign: `cinema_${domainConfiguration.cinema.id}-${domainConfiguration.cinema.name}`,
                    utmMedium: 'website'
                })}
                target={externalLinkTarget.target}
                rel={externalLinkTarget.rel}>
                <IconAppLogo className="w-24 h-24 lg:w-40 lg:h-40" />
            </a>
            <span className="flex-none">&copy; Cineamo</span>
        </div>
    );

    const footerItemLanguageSelector = (
        <div className="flex-none">
            <BasicDropdown
                className="flex-none h-36"
                openingDirection="up"
                height="custom"
                preselectedIndex={languageDropdownOptions.findIndex(
                    (it) => it.value === getLocaleFromLanguage(currentLocale)
                )}
                triggerVariant="surface"
                triggerIcon={<IconGlobe16 className="flex-none" />}
                options={languageDropdownOptions}
                onChange={(selectedItem) => onLanguageChange(selectedItem.value)}
            />
        </div>
    );

    const colorThemeIcons: Record<ColorThemePreference, React.JSX.Element> = {
        auto: <IconAutoMode24 className="w-20 h-20" />,
        light: <IconLightMode24 className="w-20 h-20" />,
        dark: <IconDarkMode24 className="w-20 h-20" />
    };

    const activeColorThemeIndex = COLOR_THEME_PREFERENCES.indexOf(selectedColorThemePreference);

    const footerItemColorThemeSelector = hasDarkStyle(domainConfiguration.websiteConfiguration) ? (
        <div
            className="relative flex-none self-start lg:self-auto flex flex-row h-36 p-[4px] rounded-16 bg-[var(--surface-color)]"
            role="radiogroup"
            aria-label={t('footer.color-theme')}>
            {/* Sliding thumb behind the active segment */}
            <div
                className="absolute top-[4px] left-[4px] w-[40px] h-[28px] rounded-16 shadow transition-transform duration-200 ease-out bg-[var(--primary-color)]"
                style={{ transform: `translateX(${activeColorThemeIndex * 100}%)` }}
            />
            {COLOR_THEME_PREFERENCES.map((preference) => {
                const isActive = selectedColorThemePreference === preference;
                return (
                    <button
                        key={`ColorThemeSwitch#${preference}`}
                        type="button"
                        className={cn(
                            'relative z-10 w-[40px] flex items-center justify-center rounded-16 transition-colors duration-200',
                            !isActive && 'clickable-element',
                            isActive ? 'text-[var(--on-primary-color)]' : 'text-[var(--on-surface-color)]'
                        )}
                        role="radio"
                        aria-checked={isActive}
                        aria-label={t(`footer.color-theme-${preference}`)}
                        title={t(`footer.color-theme-${preference}`)}
                        onClick={() => onColorThemeChange(preference)}>
                        {colorThemeIcons[preference]}
                    </button>
                );
            })}
        </div>
    ) : null;

    function footerItemLink(label: string, href: string, icon: React.JSX.Element | null): React.JSX.Element {
        const isInternal = href.startsWith('/');
        return (
            <Link
                className="clickable-element flex-none whitespace-nowrap flex flex-row items-center space-x-8"
                href={href}
                target={isInternal ? undefined : externalLinkTarget.target}
                rel={isInternal ? undefined : externalLinkTarget.rel}>
                {icon}
                <span>{label}</span>
            </Link>
        );
    }

    const { newsletterEnabled } = useNewsletterModal();
    const { formatted: phone, uri: phoneURI } = getFormattedPhoneAndURI(cinema?.phone);
    const isShowingDetailPage = currentPath.includes('/[contentId]/[showingId]');

    return (
        <>
            {cookiePreferencesModalIsOpen && (
                <CookiePreferencesModal
                    modalIsOpen={cookiePreferencesModalIsOpen}
                    setModalIsOpen={setCookiePreferencesModalIsOpen}
                    onBackClick={
                        cookiePreferencesOpenedFromCookiesModal ? () => setCookiesModalIsOpen(true) : undefined
                    }
                    privacyPolicyUrl={dataProtectionUrl('cookie_preferences_modal-data_protection')}
                />
            )}
            {cookiesModalIsOpen && (
                <CookiesModal
                    modalIsOpen={cookiesModalIsOpen}
                    setModalIsOpen={setCookiesModalIsOpen}
                    onShowCookiePreferences={onShowCookiePreferencesFromCookiesModal}
                    privacyPolicyUrl={dataProtectionUrl('cookies_modal-data_protection')}
                />
            )}
            <footer
                className={cn(
                    'relative w-full flex',
                    'lg:border-t border-[var(--surface-color)]',
                    'px-9 lg:px-0 lg:py-16 mt-auto lg:mb-0',
                    'text-[var(--on-background-color)]',
                    // Add bottom space for mobile navigation which is fixed positioned
                    // showing detail page has a fixed button "get tickets" on top of mobile navigation
                    isShowingDetailPage ? 'mb-[142px]' : 'mb-72'
                )}>
                <CenterLayout className="-mx-9 lg:mx-auto box-content">
                    <div className="lg:hidden w-full h-1 bg-[var(--surface-color)] mb-16" />
                    <div
                        className={cn(
                            'w-full flex flex-col lg:flex-row items-center text-body-12',
                            'lg:space-x-24 space-y-16 lg:space-y-0'
                        )}>
                        <MobileOrDesktopRenderer
                            mobileContent={
                                <div className="w-full lg:w-auto flex flex-col space-y-8">
                                    {footerItemLanguageSelector}
                                    {footerItemColorThemeSelector}
                                </div>
                            }
                            breakpoint="xl"
                        />
                        <div
                            className={cn(
                                'w-full lg:w-0 flex-auto min-w-0 flex flex-col lg:flex-row lg:flex-wrap',
                                'gap-y-16 lg:gap-y-12 lg:gap-x-24 lg:items-center'
                            )}>
                            <MobileOrDesktopRenderer
                                desktopContent={footerItemCineamo}
                                breakpoint="xl"
                            />
                            {(cinema.facebookLink ||
                                cinema.instagramLink ||
                                cinema.linkedinLink ||
                                cinema.xLink ||
                                cinema.whatsappLink ||
                                cinema.tiktokLink) && (
                                <div className="flex flex-row items-center space-x-16">
                                    {cinema.facebookLink && (
                                        <a
                                            className="clickable-element"
                                            href={cinema.facebookLink}
                                            aria-label="Facebook"
                                            target={externalLinkTarget.target}
                                            rel={externalLinkTarget.rel}>
                                            <IconFacebook24 />
                                        </a>
                                    )}
                                    {cinema.instagramLink && (
                                        <a
                                            className="clickable-element"
                                            href={cinema.instagramLink}
                                            aria-label="Instagram"
                                            target={externalLinkTarget.target}
                                            rel={externalLinkTarget.rel}>
                                            <IconInstagramNoBox24 />
                                        </a>
                                    )}
                                    {cinema.linkedinLink && (
                                        <a
                                            className="clickable-element"
                                            href={cinema.linkedinLink}
                                            aria-label="LinkedIn"
                                            target={externalLinkTarget.target}
                                            rel={externalLinkTarget.rel}>
                                            <IconLinkedin24 />
                                        </a>
                                    )}
                                    {cinema.xLink && (
                                        <a
                                            className="clickable-element"
                                            href={cinema.xLink}
                                            aria-label="X"
                                            target={externalLinkTarget.target}
                                            rel={externalLinkTarget.rel}>
                                            <IconX24 />
                                        </a>
                                    )}
                                    {cinema.whatsappLink && (
                                        <a
                                            className="clickable-element"
                                            href={cinema.whatsappLink}
                                            aria-label="WhatsApp"
                                            target={externalLinkTarget.target}
                                            rel={externalLinkTarget.rel}>
                                            <IconWhatsApp30 className="size-24" />
                                        </a>
                                    )}
                                    {cinema.tiktokLink && (
                                        <a
                                            className="clickable-element"
                                            href={cinema.tiktokLink}
                                            aria-label="TikTok"
                                            target={externalLinkTarget.target}
                                            rel={externalLinkTarget.rel}>
                                            <IconTiktok24 />
                                        </a>
                                    )}
                                </div>
                            )}
                            {cinema.email &&
                                footerItemLink(
                                    cinema.email,
                                    `mailto:${cinema.email}`,
                                    <IconMail24 className="w-16 h-16 flex-none" />
                                )}
                            {phone && footerItemLink(phone, phoneURI, <IconPhone24 className="w-16 h-16 flex-none" />)}
                            {newsletterEnabled && (
                                <Link
                                    href="/newsletter"
                                    className="clickable-element flex-none whitespace-nowrap flex flex-row items-center space-x-8">
                                    <IconBell24 className="w-16 h-16 flex-none" />
                                    <span>{t('footer.newsletter')}</span>
                                </Link>
                            )}
                            {footerItemLink(
                                t('footer.imprint'),
                                cinemaHasCustomImprint
                                    ? '/imprint'
                                    : addUtmParams(`${WEBAPP_URL}/imprint?locale=${currentLocale}`, {
                                          utmSource: 'cinema_website',
                                          utmContent: 'footer-imprint',
                                          utmCampaign: `cinema_${domainConfiguration.cinema.id}-${domainConfiguration.cinema.name}`,
                                          utmMedium: 'website'
                                      }),
                                null
                            )}
                            {cinemaHasTermsAndConditions &&
                                footerItemLink(
                                    t('footer.terms-and-conditions'),
                                    cinema?._embedded?.cinemaConfiguration?.websiteConfiguration
                                        ?.termsAndConditionsUrl ?? '',
                                    null
                                )}
                            {withdrawalUrl && footerItemLink(t('footer.withdrawal'), withdrawalUrl, null)}
                            {hubHref && footerItemLink(hubLabel, hubHref, null)}
                            {footerItemLink(t('footer.accessibility-statement'), '/accessibility-statement', null)}
                            {footerItemLink(
                                t('footer.data-protection'),
                                addUtmParams(`${WEBAPP_URL}/data-protection?locale=${currentLocale}`, {
                                    utmSource: 'cinema_website',
                                    utmContent: 'footer-data_protection',
                                    utmCampaign: `cinema_${domainConfiguration.cinema.id}-${domainConfiguration.cinema.name}`,
                                    utmMedium: 'website'
                                }),
                                null
                            )}
                            <button
                                className="block clickable-element text-left"
                                onClick={onDataProtectionSettingsClick}
                                aria-label={t('footer.data-protection-settings')}>
                                {t('footer.data-protection-settings')}
                            </button>
                        </div>
                        <MobileOrDesktopRenderer
                            desktopContent={
                                <div className="flex flex-row items-center space-x-8">
                                    {footerItemColorThemeSelector}
                                    {footerItemLanguageSelector}
                                </div>
                            }
                            breakpoint="xl"
                        />
                        <div className="lg:hidden w-full h-1 bg-[var(--surface-color)]" />
                        <MobileOrDesktopRenderer
                            mobileContent={footerItemCineamo}
                            breakpoint="xl"
                        />
                    </div>
                </CenterLayout>
            </footer>
        </>
    );
}
